import { Chip, MenuItem, Stack, TextField } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";

import EnhancedTable from "../../../../../components/shared/table";
import { formatNumber } from "../../../../../../infrastructure/utils/formatters";
import { ManagementWidgetShell, severityChipColor, severityLabel } from "../managementDashboardWidgets";
import { buildDrillDownHref, pick } from "../managementDashboardUtils";

const STAGE_OPTIONS = [
  { value: "", label: "همه مراحل" },
  { value: "InitialApproval", label: "بررسی اولیه" },
  { value: "SiteVisit", label: "بازدید محل" },
  { value: "Contract", label: "قرارداد" },
  { value: "Loan", label: "فرایند وام" },
  { value: "Execution", label: "اجرا" },
];

const CriticalCasesTable = ({
  loading,
  error,
  data,
  dashboardFilters,
  onReload,
}) => {
  const [stageFilter, setStageFilter] = useState("");
  const [minDays, setMinDays] = useState(7);

  const reload = useCallback(() => {
    onReload?.({
      ...dashboardFilters,
      operationalStageCode: stageFilter || undefined,
      minDaysInStage: minDays,
      sortBy: "DaysInStage",
      sortDirection: 2,
      pageNumber: 1,
      pageSize: 25,
    });
  }, [dashboardFilters, minDays, onReload, stageFilter]);

  useEffect(() => {
    reload();
  }, [reload]);

  const items = data?.items || data?.Items || [];

  const rows = useMemo(
    () =>
      items.map((item) => ({
        id: pick(item, "requestId", "RequestId"),
        referenceId: pick(item, "referenceId", "ReferenceId"),
        applicantName: pick(item, "applicantName", "ApplicantName"),
        provinceTitle: pick(item, "provinceTitle", "ProvinceTitle"),
        cityTitle: pick(item, "cityTitle", "CityTitle"),
        operationalStageTitle: pick(item, "operationalStageTitle", "OperationalStageTitle"),
        daysInStage: pick(item, "daysInStage", "DaysInStage"),
        contractorTitle: pick(item, "contractorTitle", "ContractorTitle"),
        lendingPartnerTitle: pick(item, "lendingPartnerTitle", "LendingPartnerTitle"),
        nextAction: pick(item, "nextAction", "NextAction"),
        severity: pick(item, "severity", "Severity"),
        drillHref: buildDrillDownHref(pick(item, "drillDown", "DrillDown")),
      })),
    [items]
  );

  const headCells = useMemo(
    () => [
      { id: "referenceId", label: "کد درخواست" },
      { id: "applicantName", label: "متقاضی" },
      { id: "provinceTitle", label: "استان" },
      { id: "cityTitle", label: "شهر" },
      { id: "operationalStageTitle", label: "مرحله عملیاتی" },
      {
        id: "daysInStage",
        label: "روز در مرحله",
        render: (row) => formatNumber(row.daysInStage, 1),
      },
      { id: "contractorTitle", label: "پیمانکار" },
      { id: "lendingPartnerTitle", label: "لندتک" },
      { id: "nextAction", label: "اقدام بعدی" },
      {
        id: "severity",
        label: "اولویت",
        render: (row) => (
          <Chip
            size="small"
            color={severityChipColor(row.severity)}
            label={severityLabel(row.severity)}
          />
        ),
      },
    ],
    []
  );

  return (
    <ManagementWidgetShell
      title="پرونده‌های بحرانی"
      subheader="درخواست‌های نیازمند پیگیری فوری"
      loading={loading}
      error={error}
      onRetry={reload}
    >
      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ mb: 2 }}>
        <TextField
          select
          size="small"
          label="مرحله"
          value={stageFilter}
          onChange={(e) => setStageFilter(e.target.value)}
          sx={{ minWidth: 180 }}
        >
          {STAGE_OPTIONS.map((opt) => (
            <MenuItem key={opt.value || "all"} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          type="number"
          label="حداقل روز انتظار"
          value={minDays}
          onChange={(e) => setMinDays(Number(e.target.value) || 0)}
          sx={{ width: 160 }}
        />
      </Stack>

      <EnhancedTable
        rowKey="id"
        rows={rows}
        headCells={headCells}
        defaultSort={{ orderBy: "daysInStage", order: "desc" }}
        pageSize={100}
        onRowClick={(row) => {
          if (row.drillHref) window.location.assign(row.drillHref);
        }}
      />
    </ManagementWidgetShell>
  );
};

export default CriticalCasesTable;
